import { ESTADOS_PROYECTO, MONEDA, proyectoCreateSchema, proyectoUpdateSchema } from "@uirtus/shared";
import { useState, type FormEvent } from "react";
import type { Proyecto } from "../lib/proyectos";
import { SelectorCliente, type ClienteResumen } from "./selector-cliente";
import { AreaTexto, Aviso, Boton, Campo, Entrada, Seleccion } from "./ui";

const ETIQUETA_ESTADO: Record<string, string> = {
  PROPUESTA: "Propuesta",
  ACTIVO: "Activo",
  EN_PAUSA: "En pausa",
  COMPLETADO: "Completado",
  CANCELADO: "Cancelado",
};

interface Props {
  proyecto?: Proyecto;
  /** Solo en alta: en edición el cliente del proyecto no cambia. */
  clienteInicial?: ClienteResumen;
  enviando?: boolean;
  errorServidor?: string | null;
  alEnviar: (datos: Record<string, unknown>) => void;
  alCancelar: () => void;
}

const soloNumeroDecimal = (v: string): string => v.replace(/[^\d.]/g, "");

export function FormularioProyecto({
  proyecto,
  clienteInicial,
  enviando,
  errorServidor,
  alEnviar,
  alCancelar,
}: Props) {
  const [cliente, setCliente] = useState<ClienteResumen | null>(clienteInicial ?? null);
  const [clave, setClave] = useState(proyecto?.clave ?? "");
  const [nombre, setNombre] = useState(proyecto?.nombre ?? "");
  const [descripcion, setDescripcion] = useState(proyecto?.descripcion ?? "");
  const [estado, setEstado] = useState(proyecto?.estado ?? "PROPUESTA");
  const [moneda, setMoneda] = useState<string>(proyecto?.moneda ?? "MXN");
  const [montoEstimado, setMontoEstimado] = useState(proyecto?.montoEstimado ?? "");
  const [fechaInicio, setFechaInicio] = useState(proyecto?.fechaInicio ?? "");
  const [fechaFinEstimada, setFechaFinEstimada] = useState(
    proyecto?.fechaFinEstimada ?? "",
  );
  const [errores, setErrores] = useState<Record<string, string>>({});

  function enviar(e: FormEvent) {
    e.preventDefault();

    const base = {
      clave: clave.trim() || undefined,
      nombre: nombre.trim(),
      descripcion: descripcion.trim() || undefined,
      estado,
      moneda,
      montoEstimado: montoEstimado.trim() || undefined,
      fechaInicio: fechaInicio || undefined,
      fechaFinEstimada: fechaFinEstimada || undefined,
    };

    const datos = proyecto ? base : { ...base, clienteId: cliente?.id };

    const esquema = proyecto ? proyectoUpdateSchema : proyectoCreateSchema;
    const resultado = esquema.safeParse(datos);
    if (!resultado.success) {
      const porCampo: Record<string, string> = {};
      for (const issue of resultado.error.issues) {
        const campo = String(issue.path[0] ?? "");
        if (campo && !porCampo[campo]) porCampo[campo] = issue.message;
      }
      setErrores(porCampo);
      return;
    }

    alEnviar(datos);
  }

  return (
    <form onSubmit={enviar} className="space-y-5 p-5">
      {errorServidor && <Aviso>{errorServidor}</Aviso>}

      {!proyecto ? (
        <SelectorCliente valor={cliente} alSeleccionar={setCliente} error={errores["clienteId"]} />
      ) : (
        <Campo etiqueta="Cliente">
          <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm dark:border-slate-800 dark:bg-slate-800/50">
            {clienteInicial?.razonSocial}
          </div>
        </Campo>
      )}

      <div className="grid gap-4 sm:grid-cols-[10rem_1fr]">
        <Campo
          etiqueta="Clave"
          error={errores["clave"]}
          ayuda="Opcional, para referenciarlo rápido"
        >
          <Entrada
            value={clave}
            onChange={(e) => setClave(e.target.value.toUpperCase())}
            placeholder="ACME-ERP"
            className="font-mono uppercase"
          />
        </Campo>
        <Campo etiqueta="Nombre" requerido error={errores["nombre"]}>
          <Entrada
            value={nombre}
            onChange={(e) => setNombre(e.target.value)}
            error={!!errores["nombre"]}
          />
        </Campo>
      </div>

      <Campo etiqueta="Descripción" error={errores["descripcion"]}>
        <AreaTexto rows={3} value={descripcion} onChange={(e) => setDescripcion(e.target.value)} />
      </Campo>

      <div className="grid gap-4 sm:grid-cols-3">
        <Campo etiqueta="Estado" error={errores["estado"]}>
          <Seleccion value={estado} onChange={(e) => setEstado(e.target.value as never)}>
            {ESTADOS_PROYECTO.map((e) => (
              <option key={e} value={e}>
                {ETIQUETA_ESTADO[e]}
              </option>
            ))}
          </Seleccion>
        </Campo>
        <Campo etiqueta="Moneda" error={errores["moneda"]}>
          <Seleccion value={moneda} onChange={(e) => setMoneda(e.target.value)}>
            {Object.entries(MONEDA)
              .filter(([c]) => c !== "XXX")
              .map(([c, nombreMoneda]) => (
                <option key={c} value={c}>
                  {c} · {nombreMoneda}
                </option>
              ))}
          </Seleccion>
        </Campo>
        <Campo etiqueta="Monto estimado" error={errores["montoEstimado"]}>
          <Entrada
            value={montoEstimado}
            onChange={(e) => setMontoEstimado(soloNumeroDecimal(e.target.value))}
            inputMode="decimal"
            placeholder="0.00"
            className="font-mono"
          />
        </Campo>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <Campo etiqueta="Inicio" error={errores["fechaInicio"]}>
          <Entrada
            type="date"
            value={fechaInicio}
            onChange={(e) => setFechaInicio(e.target.value)}
          />
        </Campo>
        <Campo etiqueta="Fin estimado" error={errores["fechaFinEstimada"]}>
          <Entrada
            type="date"
            value={fechaFinEstimada}
            onChange={(e) => setFechaFinEstimada(e.target.value)}
          />
        </Campo>
      </div>

      <div className="flex justify-end gap-2 border-t border-slate-200 pt-4 dark:border-slate-800">
        <Boton type="button" variante="secundario" onClick={alCancelar}>
          Cancelar
        </Boton>
        <Boton type="submit" disabled={enviando}>
          {enviando ? "Guardando…" : proyecto ? "Guardar cambios" : "Crear proyecto"}
        </Boton>
      </div>
    </form>
  );
}
